const HERO_IMAGE =
  'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?auto=format&fit=crop&w=1600&q=80';

const RELATED_GUIDES = [
  {
    slug: 'goalkeeper-achilles-soleus-pain-blood-flow-loading',
    title:
      'Goalkeeper Achilles & Soleus Pain: Blood-Flow Warm-Up & Gradual Tendon Loading',
    blurb: 'circulation warm-up to run after taping, before kickoff.',
  },
  {
    slug: 'soleus-pain-kt-tape-soccer-guide',
    title: 'Soleus Pain in Soccer: How to Use KT Tape & Keep Playing Safely',
    blurb: 'general outfield soleus taping and return-to-play rules.',
  },
  {
    slug: 'intermediate-soccer-goalkeeper-practice-warm-up-wall-setup',
    title: 'Intermediate Goalkeeper Practice: Warm-Up, Wall Setup & Session Ideas',
    blurb: 'full GK warm-up once you are cleared to play.',
  },
  {
    slug: 'soccer-field-injury-remedies-calf-hamstring',
    title: 'On-Field Remedies for Soccer Calf, Soleus & Hamstring Injuries',
    blurb: 'first aid if pain spikes during the match.',
  },
];

const REFERENCE_LINKS = [
  {
    label: 'KT Tape — Achilles tendonitis instructions',
    url: 'https://www.kttape.com/blogs/instructions/achilles-tendonitis',
  },
  {
    label: 'KT Tape — calf / soleus application guide',
    url: 'https://www.kttape.com/blogs/instructions/calf',
  },
  {
    label: 'American Academy of Orthopaedic Surgeons — Achilles tendinitis',
    url: 'https://orthoinfo.aaos.org/en/diseases--conditions/achilles-tendinitis/',
  },
];

/** Pre-game KT Tape for goalkeeper soleus / Achilles injury */
export const GOALKEEPER_SOLEUS_ACHILLES_KT_TAPE_POST = {
  id: 'featured-goalkeeper-soleus-achilles-kt-tape',
  slug: 'goalkeeper-soleus-achilles-kt-tape-pre-game',
  title:
    'How to Apply Achilles KT Tape for a Soleus Injury Before a Soccer Goalkeeper Match',
  excerpt:
    'Mild soleus or Achilles ache and you are in goal tonight? Step-by-step kinesiology tape for keepers—bent-knee soleus strips plus Achilles support, applied 45–60 minutes before kickoff, with video tutorials and a post-tape warm-up checklist.',
  publishedAt: '2026-07-31T16:30:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'GK Training Desk',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'Related goalkeeper & wellness guides',
  relatedPosts: RELATED_GUIDES,
  referenceLinks: REFERENCE_LINKS,
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '45–60 min', label: 'Apply before kickoff' },
        { value: '3 strips', label: 'GK soleus + Achilles setup' },
        { value: 'Knee bent', label: 'Critical taping position' },
        { value: 'Mild pain only', label: 'Not for acute tears' },
      ],
    },
    {
      type: 'panel',
      title: 'Who this guide is for',
      paragraphs: [
        'You have a **mild soleus strain** or **Achilles irritation** — a dull ache low on the calf or along the tendon — and you are **starting in goal** today. A clinician has not told you to sit out, you can walk without a limp, and you want **kinesiology tape (KT Tape)** for support and sensory feedback before the match.',
        'This is a **pre-game taping guide** written for goalkeepers. Keepers load the soleus differently from outfield players: bent-knee set stance, repeated small push-offs, jumps for crosses, and hard landings from dives. The tape setup below supports **both the soleus muscle belly and the Achilles tendon path** — then you follow with a blood-flow warm-up, not aggressive stretching.',
        'This is not medical advice. Sharp pain, swelling, bruising, inability to rise on your toes, or a sudden pop means **do not tape and play** — see a sports medicine clinician.',
      ],
      chips: [
        'Goalkeepers',
        'Pre-game taping',
        'Soleus + Achilles',
        'KT Tape',
        'Not medical advice',
      ],
    },
    {
      type: 'panel',
      title: 'Pre-game timeline — when to tape as a goalkeeper',
      orderedList: [
        '**T–60 min** — Shower if needed, then dry skin completely. No lotion on the calf or ankle.',
        '**T–50 min** — Apply tape in a quiet spot (car, locker room, sideline). Sit with knee bent.',
        '**T–45 min** — Rub all strips 60 seconds to activate adhesive. Put on socks loosely to protect edges.',
        '**T–30 min** — Begin blood-flow warm-up: easy jog or bike, ankle pumps, lateral shuffles.',
        '**T–15 min** — GK footwork and 4–6 gentle catches. One controlled collapse save per side only if pain-free.',
        '**T–5 min** — Gloves on, organize wall assignments, final bent-knee heel raises (2 × 8).',
        '**Kickoff** — Pain should stay ≤ 3/10. If it climbs in the first 10 minutes, signal for a sub.',
      ],
      callout:
        'KT Tape adheres best when applied **at least 45–60 minutes before** sweating. Taping five minutes before kickoff is the most common reason it peels by halftime.',
    },
    {
      type: 'panel',
      title: 'What you need',
      list: [
        '**Kinesiology tape** — KT Tape, RockTape, or equivalent (3 full strips plus 1 half strip).',
        '**Scissors** — round the corners on cut strips so they do not catch on socks or pants.',
        '**Alcohol wipe or dry towel** — remove sweat, turf pellets, and sunscreen.',
        '**Chair or bench** — you will tape with knee bent ~20–30° and foot flat.',
        '**Optional compression sleeve** — worn over tape if your league allows; not so tight it numbs the foot.',
      ],
    },
    {
      type: 'panel',
      title: 'Step-by-step: goalkeeper soleus + Achilles KT Tape (three-strip method)',
      intro:
        'Tape the **soleus with a bent knee** (your match stance), then add **Achilles support** with light dorsiflexion. All anchors: **zero stretch**. Middle of strips: **15–50% stretch** depending on step — never max tension.',
      orderedList: [
        '**Position** — Sit with the injured leg bent 20–30°, foot flat on the floor. This is how your soleus sits during a keeper set stance — not straight-legged.',
        '**Strip 1 — Soleus decompression (I-strip)** — Tear backing in the middle. Anchor the center on the most tender spot on the **lower calf** (soleus). Lay tails toward the knee and toward the Achilles with **no stretch** on the last 2 inches of each end. Light **15–25% stretch** only in the middle over the pain point.',
        '**Strip 2 — Soleus support (Y or lateral I)** — Anchor near the Achilles (no stretch). Run up the inner or outer lower calf over the soleus with **15% stretch**. Finish below the knee (anchor, no stretch). Mirrors the bent-knee load path for keepers.',
        '**Strip 3 — Achilles longitudinal** — Point toes toward shin **only as far as pain-free** (light dorsiflexion). Anchor on the **arch side of the heel** (ahead of the heel pad). Peel backing and run the strip up along the **Achilles tendon** with **25–50% stretch** (half of max tape stretch). Anchor below the calf belly with **zero stretch**. Rub down firmly.',
        '**Strip 4 (optional) — Cross-strip at pain site** — Half strip placed **horizontally** across the most painful point on the Achilles or lower soleus with **50–80% stretch** in the center, zero stretch on both ends. Skip if you are new to taping — Strips 1–3 are enough for most mild cases.',
        '**Activate** — Rub every strip 30–60 seconds. Body heat improves stick.',
        '**Test** — Stand in keeper ready stance (knees bent). Do 5 slow bent-knee heel raises, 3 lateral shuffles each way, and one small forward step to a cone. Re-tape loose edges before warm-up.',
      ],
      callout:
        'Goalkeeper mistake: taping with a straight leg then dropping into a low set stance. The tape bunches and peels in the first 15 minutes. Always tape in **bent-knee ready position**.',
    },
    {
      type: 'table',
      title: 'Which strip goes where — soleus vs. Achilles pain',
      headers: ['Your symptom', 'Primary strip', 'Secondary strip', 'GK note'],
      rows: [
        [
          'Deep ache in lower calf (bent knee hurts most)',
          'Strip 1 + 2 on soleus',
          'Strip 3 light if Achilles also tight',
          'Common in set stance and distribution follow-through',
        ],
        [
          'Tenderness along Achilles tendon',
          'Strip 3 longitudinal',
          'Strip 4 cross-strip at pain point',
          'Felt on push-off and landing from low dives',
        ],
        [
          'Both soleus and Achilles (common in keepers)',
          'All three main strips',
          'Optional Strip 4',
          'Use full method above; do not skip bent-knee soleus strips',
        ],
        [
          'Pain only when jumping for crosses',
          'Strip 2 + 3',
          'Limit high claims in match even with tape',
          'Tape supports; it does not fix fatigue overload',
        ],
      ],
    },
    {
      type: 'videoList',
      title: 'Video tutorials — Achilles & soleus KT Tape application',
      intro:
        'Watch these before your first self-application. Adapt each video for a **bent-knee soleus setup** and apply **45–60 minutes before** your match.',
      videos: [
        {
          title: 'How to Tape for Achilles Tendonitis: A Step-by-Step Guide',
          channel: 'Physio and training channel',
          description:
            'Longitudinal Achilles strip from heel anchor with dorsiflexion — matches Strip 3 in this guide. Use 50–70% stretch on the tendon path, zero stretch on anchors.',
          url: 'https://www.youtube.com/watch?v=ua6EvmB8eng',
        },
        {
          title: 'KT Tape: Calf application',
          channel: 'KT Tape',
          description:
            'Official two-strip calf method — apply with knee bent 20–30° for soleus focus (Strips 1 and 2).',
          url: 'https://www.youtube.com/watch?v=elvYXZUEMDo',
        },
        {
          title: 'Calf strain (gastrocnemius/soleus) kinesiology tape',
          channel: 'John Gibbons',
          description:
            'Physio-led decompression taping over the painful calf area — useful for Strip 1 soleus placement and stretch percentage.',
          url: 'https://www.youtube.com/watch?v=u-bRY2TMMZ8',
        },
        {
          title: 'KT Tape: Heel',
          channel: 'KT Tape',
          description:
            'Heel and Achilles path taping with anchor/stretch/anchor technique — helpful reference for heel and tendon strip placement.',
          url: 'https://www.youtube.com/watch?v=O68dZPASlPA',
        },
        {
          title: 'KT Tape — Get Ready To Tape (basics)',
          channel: 'KT Tape',
          description:
            'Skin prep, anchor/stretch/anchor method, and safe removal — watch first if you are new to kinesiology tape.',
          url: 'https://www.youtube.com/watch?v=KesPHFirD7Q',
        },
      ],
    },
    {
      type: 'table',
      title: 'Pre-game taping mistakes goalkeepers make',
      headers: ['Mistake', 'What happens in goal', 'Fix'],
      rows: [
        [
          'Taping 5 minutes before kickoff',
          'Tape rolls off during warm-up jog; gone by first corner',
          'Apply 45–60 min early; rub strips 60 sec',
        ],
        [
          'Straight-leg soleus taping',
          'Bunches when you sink into set stance',
          'Sit with knee bent 20–30°, foot flat',
        ],
        [
          'Max stretch over the tendon',
          'Numbness, pinching, or worse pain on first dive',
          '25–50% on longitudinal strip; light on soleus',
        ],
        [
          'Stretching anchor ends',
          'Edges peel when you pull on socks or long pants',
          'Last 2 inches of every strip: zero stretch',
        ],
        [
          'Taping over lotion or wet skin',
          'Tape lifts on first sweat',
          'Dry skin; alcohol wipe if needed',
        ],
        [
          'Skipping post-tape warm-up',
          'Cold tendon + explosive first save',
          '12–15 min blood-flow routine after taping',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'After taping — 12-minute goalkeeper warm-up',
      intro:
        'Do this after tape has set. Pair with our blood-flow guide for full detail. No aggressive wall calf stretches.',
      orderedList: [
        '**3 min** — Easy jog, bike, or brisk walk.',
        '**2 min** — Ankle pumps and marching in place.',
        '**2 min** — Lateral shuffles in keeper-ready height.',
        '**2 min** — Bent-knee heel raises: 2 × 10 slow.',
        '**2 min** — Footwork box: shuffle → forward → shuffle → back.',
        '**1 min** — 3 quick steps to each post from set position.',
        '**Optional** — 4–6 catches from 8 yards; one controlled collapse save per side if pain-free.',
      ],
      callout:
        'Tape gives support and feedback — the warm-up gives **blood flow**. You need both before standing in goal for 45+ minutes.',
    },
    {
      type: 'panel',
      title: 'Playing in goal with tape — match-day habits',
      list: [
        '**Stand slightly taller** than your usual low crouch early in the match — deep soleus flexion loads the taped area continuously.',
        '**Punch or catch** at a comfortable height instead of jumping for every cross in the first half.',
        '**Smother early on 1v1s** rather than late explosive extension.',
        '**Fewer driven long balls** — use rolls and short throws when possible.',
        '**Check tape at halftime** — re-rub edges; add a small strip only if an edge lifted (do not re-tape a wet leg mid-game unless necessary).',
        '**Come off if pain rises** — tape is not a permission slip for a worsening tear.',
      ],
    },
    {
      type: 'panel',
      title: 'When NOT to tape and play',
      list: [
        'Sharp or stabbing pain on the first push-off or landing.',
        'Visible swelling, bruising, or warmth along the tendon.',
        'Inability to do 10 single-leg bent-knee heel raises before the match.',
        'Pain that was getting worse each day this week — not stable mild ache.',
        'Numbness, tingling, or calf pain with shortness of breath (seek urgent care).',
        'Any suspicion of **Achilles rupture** — sudden pop, gap in tendon, cannot rise on toes.',
        'First 48–72 hours after an acute strain with swelling — rest and ice, not tape-for-play.',
      ],
    },
    {
      type: 'panel',
      title: 'Removing tape after the match',
      orderedList: [
        'Shower in warm water for 5 minutes — adhesive softens.',
        'Peel slowly in the direction of hair growth, staying close to the skin.',
        'Use baby oil or tape remover if stuck; do not rip.',
        'Wash skin, moisturize if dry, and assess pain — note any increase for your clinician.',
        'Do not re-tape the same irritated skin night after night without a rest day.',
      ],
    },
    {
      type: 'panel',
      title: 'When tape is not enough',
      paragraphs: [
        'If you need KT Tape to get through **every** match for more than two weeks, you have a **rehab problem**, not a taping problem. Book a physio for bent-knee soleus strengthening, eccentric heel drops, and a return-to-diving plan.',
        'For off-field loading progressions and a full return-to-GK checklist, see the companion blood-flow guide linked below.',
      ],
      callout:
        'The best tape job is the one you eventually do not need. Build soleus and Achilles capacity in training so match day is not the stress test.',
    },
  ],
};
